// Phase 2 (V2 unlock) — V2.5 deferral grey-out wrapper.
//
// Wraps any UI element to mark it as "Coming in V2.5":
//   • 50% opacity over the children
//   • Subtle lock overlay (lucide Lock icon)
//   • Small "V2.5" pill in the top-right corner
//   • Click anywhere on the wrapper opens a modal explaining what's deferred
//
// Usage:
//   <ComingInV25 description="Real-time voice calls land in V2.5 with Vapi.">
//     <VoiceNode />
//   </ComingInV25>
//
// Pass `inline` for thin UI bits (a toggle, a checkbox) where you don't want
// the full overlay treatment — only the badge + click-to-modal.
import PropTypes from "prop-types";
import { useState } from "react";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function ComingInV25({ children, description, title = "Coming in V2.5", inline = false, className }) {
  const [open, setOpen] = useState(false);

  const openModal = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (e && e.preventDefault) e.preventDefault();
    setOpen(true);
  };

  if (inline) {
    return (
      <>
        <button
          type="button"
          onClick={openModal}
          className={cn("inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-1.5 py-0.5 text-[10px] font-semibold cursor-pointer hover:bg-amber-100", className)}
          data-testid="coming-v25-badge-inline"
        >
          <Lock size={9} /> V2.5
        </button>
        <V25Modal open={open} onOpenChange={setOpen} title={title} description={description} />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        aria-label={`${title} — click for details`}
        className={cn("relative cursor-pointer select-none text-left block w-full appearance-none", className)}
        data-testid="coming-v25-wrapper"
      >
        {/* Greyed children */}
        <div className="opacity-50 pointer-events-none">{children}</div>

        {/* Centered lock */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-9 w-9 rounded-full bg-white/85 ring-1 ring-slate-200 shadow-sm flex items-center justify-center backdrop-blur-sm">
            <Lock size={14} className="text-slate-600" />
          </div>
        </div>

        {/* Pill */}
        <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-1.5 py-0.5 text-[10px] font-semibold pointer-events-none">
          <Lock size={9} /> V2.5
        </span>
      </button>
      <V25Modal open={open} onOpenChange={setOpen} title={title} description={description} />
    </>
  );
}

function V25Modal({ open, onOpenChange, title, description }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="coming-v25-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-amber-50 text-amber-700 ring-1 ring-amber-200 flex items-center justify-center">
              <Lock size={14} />
            </div>
            <DialogTitle className="text-[16px] font-semibold tracking-tight text-slate-900">{title}</DialogTitle>
          </div>
          <DialogDescription className="mt-2 text-[12.5px] text-slate-600 leading-relaxed">
            {description || "This capability is on the V2.5 roadmap. The UI is here so you can see how it'll feel, but the engine ships next quarter."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end mt-2">
          <Button size="sm" onClick={() => onOpenChange(false)} data-testid="coming-v25-modal-close">Got it</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

ComingInV25.propTypes = {
  children: PropTypes.node,
  description: PropTypes.string,
  title: PropTypes.string,
  inline: PropTypes.bool,
  className: PropTypes.string,
};

V25Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
};

export default ComingInV25;
